// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { fontConfig } from "@bg/XDL/PreStyle/typography";
import { AccordionPresentationProps } from "@insite/mobius/Accordion";
import { AccordionSectionPresentationProps } from "@insite/mobius/AccordionSection";
import { FormFieldClickable } from "@insite/mobius/FormField";
import { GridContainerProps } from "@insite/mobius/GridContainer";
import { GridItemProps } from "@insite/mobius/GridItem";
import { LinkPresentationProps } from "@insite/mobius/Link";
import { TextFieldPresentationProps } from "@insite/mobius/TextField";
import { TypographyProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss, { StyledProp } from "@insite/mobius/utilities/InjectableCss";
import React from "react";
import { css } from "styled-components";

export const pageTitlesPrintStyle = () => {
    return css`
        @media print {
            margin-bottom: 20px;
            margin-top: 20px;
        }
    `;
};

export const boxContentStyle = css`
    width: 100%;
    padding: 30px;
    background: ${getColor("common.ui4")};
    @media print {
        background: #f0f0f2;
    }
`;

export const linkIconStyleGenerator = ({
    icon,
    textSize,
    iconSize,
    additionalTypographyCss,
    additionalIconCss,
    wrapperCss,
}: {
    icon: React.ComponentType | string;
    textSize: string;
    iconSize: number;
    additionalTypographyCss?: StyledProp<any>;
    additionalIconCss?: StyledProp<any>;
    wrapperCss?: StyledProp<any>;
}) =>
    <LinkPresentationProps>{
        color: "secondary.main",
        css: css`
            &&:hover,
            &&:focus {
                color: ${getColor("secondary.main")} !important;
                text-decoration: underline;
            }
            ${wrapperCss}
        `,
        typographyProps: {
            css: css`
                font-size: ${textSize || `${fontConfig.fontSize.xSmall}px`};
                line-height: ${fontConfig.lineHeight.small};
                color: ${getColor("secondary.main")} !important;
                &&:hover,
                &&:focus {
                    text-decoration: underline;
                }
                ${additionalTypographyCss}
            `,
        },
        icon: {
            iconProps: {
                src: icon,
                color: "secondary",
                size: iconSize || 17,
                css: css`
                    margin-right: 5px;
                    fill: ${getColor("secondary.main")};
                    ${additionalIconCss}
                `,
            },
            position: "left",
        },
    };

export interface OrderDetailsStyles {
    leftGridItem?: InjectableCss;
    leftTopGridItem?: InjectableCss;
    leftBottomGridItem?: InjectableCss;
    rightGridItem?: InjectableCss;
    topGridItem?: InjectableCss;
    wrapper?: InjectableCss;
}

// Used by saved order details and order history details
export const orderDetailsStyle: OrderDetailsStyles = {
    wrapper: {
        css: css`
            display: flex;
            @media screen and (max-width: 1200px) {
                flex-direction: column;
            }
            @media print {
                display: block;
            }
        `,
    },
    rightGridItem: {
        css: css`
            width: 326px;
            padding-left: 20px;
            margin-top: 20px;
            @media screen and (max-width: 1200px), print {
                width: 100%;
                padding-left: 0;
            }
        `,
    },
    leftGridItem: {
        css: css`
            width: calc(100% - 326px);
            @media screen and (max-width: 1200px), print {
                width: 100%;
            }
        `,
    },
    leftTopGridItem: {
        css: css`
            padding: 30px 0;
            background-color: ${getColor("common.white")};
            border-top: 2px solid ${getColor("common.ui3")};
            border-bottom: 2px solid ${getColor("common.ui3")};
            margin-bottom: 32px;
            margin-top: 15px;
            > div {
                ${boxContentStyle}
            }
            @media screen and (max-width: 768px) {
                padding: 35px 10px;
            }

            @media print {
                border-top: 2px solid #dddde5;
                border-bottom: 2px solid #dddde5;
            }
        `,
    },
    leftBottomGridItem: {
        css: css`
            margin-bottom: 32px;
        `,
    },
    topGridItem: {
        css: css`
            margin-bottom: 32px;
        `,
    },
};

export interface InfoDisplayStyles {
    wrapper?: InjectableCss;
    titleText?: TypographyProps;
    contentText?: TypographyProps;
    editLink?: InjectableCss;
    editLinkWrapper?: InjectableCss;
}

// Used by saved order details and order history details
export const infoDisplayStyles: InfoDisplayStyles = {
    wrapper: {
        css: css`
            margin-bottom: 5px;
            width: 100%;
            display: flex;
            flex-direction: column;
        `,
    },
    titleText: {
        css: css`
            margin-bottom: 0;
            padding-right: 2px;
            font-size: 16px;
            font-weight: ${fontConfig.weight.bold};
            letter-spacing: 0;
            line-height: ${fontConfig.lineHeight.regular};
            text-transform: uppercase;
        `,
    },
    contentText: {
        css: css`
            font-size: 16px;
        `,
    },
};

export const countText = css`
    font-size: 22px;
    font-weight: ${fontConfig.weight.semiBold};
    letter-spacing: 0;
    line-height: ${fontConfig.lineHeight.small};
`;

export const generateSearchTextFieldStylesWithLeftIcon = (
    props?: TextFieldPresentationProps,
): TextFieldPresentationProps => ({
    ...props,
    cssOverrides: {
        ...props?.cssOverrides,
        formInputWrapper: css`
            ${props?.cssOverrides?.formInputWrapper}
            ${FormFieldClickable as any}, span[class^="IconWrapper"] {
                left: 2px;
            }
            &&&&&& input {
                padding: 0 10px 0 36px;
            }
        `,
    },
    iconProps: {
        ...props?.iconProps,
        src: "Search",
        css: css`
            ${props?.iconProps?.css}
            &&&&&& {
                left: 2px;
                top: ${BLUEPRINT_NAME.toLowerCase() === "bgs" ? "5px;" : "2px;"};
            }

            svg {
                width: 24px;
                height: 24px;
            }
        `,
    },
});

// order history details
export const orderTotalSummary: InfoDisplayStyles = {
    wrapper: {
        css: css`
            margin-bottom: 5px;
            width: 100%;
            display: flex;
            flex-direction: column;
            @media print {
                display: block;
            }
        `,
    },
    titleText: {
        variant: "h5",
        css: css`
            margin-bottom: 0;
        `,
    },
    contentText: {
        css: css`
            font-size: 16px;
        `,
    },
};

// Used by order history details
export const infoDisplayStylesTwo: InfoDisplayStyles = {
    titleText: {
        variant: "h5",
        as: "h5",
    },
    editLink: {
        css: css`
            color: ${getColor("secondary.main")};
            span {
                color: ${getColor("secondary.main")};
            }
            font-size: 16px;
            letter-spacing: 0;
            line-height: ${fontConfig.lineHeight.regular};
        `,
    },
    editLinkWrapper: {
        css: css`
            display: flex !important;
            flex-direction: row !important;
            svg {
                width: 16px;
                margin: 2px;
                margin-left: 13px;
                height: 16px;
                path {
                    fill: ${getColor("secondary.main")};
                }
            }
        `,
    },
};

export interface FilterHeaderStyles {
    container?: GridContainerProps;
    toggleFilterGridItem?: GridItemProps;
    wrappingAccordion?: AccordionPresentationProps;
    toggleFilterClickable?: AccordionSectionPresentationProps;
}

export const filterHeaderStyles: FilterHeaderStyles = {
    container: {
        gap: 8,
        css: css`
            padding-bottom: 20px;
        `,
    },
    toggleFilterGridItem: {
        width: 12,
        style: { justifyContent: "flex-end" },
    },
    wrappingAccordion: {
        css: css`
            width: 100%;

            && dd {
                padding: 0;
            }
            button {
                span {
                    font-size: 17px !important;
                }
            }
        `,
    },
    toggleFilterClickable: {
        css: css`
            width: 100%;
            padding: 0;
        `,
    },
};
